/**
 * AdminDashboardScreen — Centro de control Nilabs.
 *
 * Llama dashboard() al montar y muestra:
 *   1. KPI grid (4 cards prominentes: usuarios totales, activos 30d, MRR, planes/mes)
 *   2. Distribución por tier (mini chart de barras horizontales con count + %)
 *   3. Conversión funnel (% free → paid, % activos / totales)
 *   4. Top usuarios por planes creados (top 5 con avatar + nombre + count)
 *
 * El backend NO devuelve "actividad reciente" ni "delta vs mes anterior", así
 * que mostramos lo que sí tenemos (vistas totales de share, conversiones puras).
 *
 * Para el "Top usuarios" hacemos un fetch extra a listUsers ordenado por
 * created_at desc (el backend no expone "ordenar por plans_count"), y luego
 * los ordenamos client-side por plans_count desc.
 */
import { useCallback, useEffect, useState } from "react";
import { Avatar, EmptyState } from "../../lib/components";
import { Icon } from "../../lib/Icon";
import {
  dashboard,
  listUsers,
  type AdminDashboardKpis,
  type AdminUserSummary,
} from "../../api/admin";
import {
  AdminBadge,
  AdminGate,
  Skeleton,
  colorFor,
  formatCOP,
  initialsOf,
  tierLabel,
} from "./_shared";

export function AdminDashboardScreen() {
  return (
    <AdminGate>
      <Inner />
    </AdminGate>
  );
}

function Inner() {
  const [kpis, setKpis] = useState<AdminDashboardKpis | null>(null);
  const [topUsers, setTopUsers] = useState<AdminUserSummary[] | null>(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setErr(null);
    try {
      const [k, users] = await Promise.all([
        dashboard(),
        // Best-effort: traemos hasta 500 para luego ordenar por plans_count.
        // Si el backend tiene >500 coaches este top será incompleto pero
        // suficiente como aproximación visual; un endpoint dedicado sería mejor.
        listUsers({ limit: 500, offset: 0 }).catch(() => [] as AdminUserSummary[]),
      ]);
      setKpis(k);
      setTopUsers(
        users.slice().sort((a, b) => b.plans_count - a.plans_count).slice(0, 5),
      );
    } catch (e) {
      setErr(e instanceof Error ? e.message : String(e));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  if (loading && !kpis) {
    return (
      <div className="page al-admin-shell">
        <Header />
        <div className="al-kpi-grid al-admin-kpi-grid">
          <Skeleton height={96} /><Skeleton height={96} /><Skeleton height={96} /><Skeleton height={96} />
        </div>
        <div className="card" style={{ padding: 16 }}>
          <Skeleton height={140} />
        </div>
      </div>
    );
  }

  if (err && !kpis) {
    return (
      <div className="page al-admin-shell" style={{ paddingTop: 32 }}>
        <Header />
        <EmptyState
          icon="settings"
          title="No pude cargar los KPIs"
          body={err}
          action={
            <button className="btn btn-primary" onClick={load}>
              Reintentar
            </button>
          }
        />
      </div>
    );
  }

  if (!kpis) return null;

  // Distribución por tier (orden fijo para que el chart sea estable).
  const tierOrder = ["free", "solo", "pro", "studio"];
  const tiers = Object.entries(kpis.users_by_tier)
    .map(([k, v]) => [k, Number(v) || 0] as [string, number])
    .sort((a, b) => {
      const ai = tierOrder.indexOf(a[0]);
      const bi = tierOrder.indexOf(b[0]);
      if (ai !== -1 && bi !== -1) return ai - bi;
      if (ai !== -1) return -1;
      if (bi !== -1) return 1;
      return b[1] - a[1];
    });
  const tierMax = Math.max(1, ...tiers.map(([, n]) => n));
  const totalUsers = kpis.users_total || 1;

  // Funnel
  const conversionRate = totalUsers > 0 ? (kpis.conversions_free_to_solo / totalUsers) * 100 : 0;
  const activeRate = totalUsers > 0 ? (kpis.users_active_30d / totalUsers) * 100 : 0;

  return (
    <div className="page al-admin-shell">
      <Header />

      {/* ----- KPIs principales ----- */}
      <div className="al-kpi-grid al-admin-kpi-grid">
        <div className="card al-kpi al-admin-kpi">
          <div className="stat-label">Usuarios totales</div>
          <div className="stat-value">{kpis.users_total}</div>
          <div className="tiny muted">{kpis.conversions_free_to_solo} con al menos 1 pago</div>
        </div>

        <div className="card al-kpi al-admin-kpi">
          <div className="stat-label">Activos últimos 30d</div>
          <div className="stat-value">{kpis.users_active_30d}</div>
          <div className="tiny muted">{Math.round(activeRate)}% del total</div>
        </div>

        <div className="card al-kpi al-admin-kpi">
          <div className="stat-label">MRR estimado</div>
          <div className="stat-value">{formatCOP(kpis.mrr_active_cop)}</div>
          <div className="tiny muted">tier × precio mensual</div>
        </div>

        <div className="card al-kpi al-admin-kpi">
          <div className="stat-label">Planes creados (30d)</div>
          <div className="stat-value">{kpis.plans_created_30d}</div>
          <div className="tiny muted">{kpis.plans_total} totales en la plataforma</div>
        </div>
      </div>

      {/* ----- Distribución por tier + Funnel lado a lado ----- */}
      <div className="al-admin-row">
        <div className="card al-admin-card">
          <div className="card-header">
            <div className="card-title">Distribución por tier</div>
          </div>
          {tiers.length === 0 ? (
            <div className="muted tiny">Sin datos</div>
          ) : (
            <div className="al-admin-bars">
              {tiers.map(([t, n]) => {
                const pct = totalUsers > 0 ? (n / totalUsers) * 100 : 0;
                const widthPct = (n / tierMax) * 100;
                return (
                  <div key={t} className="al-admin-bar-row">
                    <div className="al-admin-bar-label">{tierLabel(t)}</div>
                    <div className="al-admin-bar-track">
                      <div
                        className="al-admin-bar-fill"
                        style={{ width: `${widthPct}%`, background: colorFor(t) }}
                      />
                    </div>
                    <div className="al-admin-bar-value">
                      {n} <span className="muted">({pct.toFixed(0)}%)</span>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        <div className="card al-admin-card">
          <div className="card-header">
            <div className="card-title">Conversión</div>
          </div>
          <div className="al-admin-funnel">
            <FunnelStep
              label="Free → Pagados"
              value={`${conversionRate.toFixed(1)}%`}
              hint={`${kpis.conversions_free_to_solo} de ${kpis.users_total}`}
            />
            <FunnelStep
              label="Activos / Total"
              value={`${activeRate.toFixed(1)}%`}
              hint={`${kpis.users_active_30d} editaron en 30d`}
            />
            <FunnelStep
              label="Vistas de planes"
              value={String(kpis.shares_views_total)}
              hint={`${kpis.shares_total} share-links creados`}
            />
            <FunnelStep
              label="Pagos último mes"
              value={formatCOP(kpis.payments_total_cop)}
              hint={`${kpis.payments_total_count} pagos totales registrados`}
            />
          </div>
        </div>
      </div>

      {/* ----- Top usuarios por planes ----- */}
      <div className="card al-admin-card">
        <div className="card-header">
          <div className="card-title">Top coaches por planes creados</div>
          <span className="tiny muted">top 5 (de los primeros 500)</span>
        </div>
        {topUsers && topUsers.length > 0 ? (
          <div className="al-admin-top">
            {topUsers.map((u, idx) => (
              <div className="al-admin-top-row" key={u.id}>
                <div className="al-admin-top-rank">#{idx + 1}</div>
                <Avatar
                  user={{
                    initials: initialsOf(u.name, u.email),
                    avatar_color: colorFor(u.email),
                  }}
                />
                <div className="al-admin-top-info">
                  <div className="al-admin-top-name">{u.name || u.email}</div>
                  <div className="tiny muted">{u.email}</div>
                </div>
                <div className="al-admin-top-meta">
                  <span className="chip">{tierLabel(u.plan_tier)}</span>
                  <b>{u.plans_count}</b>
                  <span className="muted tiny">planes</span>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="muted tiny">Aún no hay coaches con planes.</div>
        )}
      </div>

      {/* ----- Honestidad: lo que aún no tenemos ----- */}
      <div className="card al-admin-card al-admin-soft">
        <div className="card-title">
          <Icon name="sparkle" size={14} /> Próximamente
        </div>
        <p className="tiny muted" style={{ marginTop: 6 }}>
          Actividad reciente (signups, upgrades en vivo), deltas vs mes anterior, cohort
          retention y churn aún no están en el endpoint /admin/dashboard. Mientras tanto
          puedes revisar la tabla completa de usuarios y exportar a CSV desde Reportes.
        </p>
      </div>
    </div>
  );
}

function Header() {
  return (
    <div className="al-admin-head">
      <div>
        <h1 className="al-page-title">
          Centro de control <AdminBadge />
        </h1>
        <p className="muted">KPIs globales y salud del producto.</p>
      </div>
    </div>
  );
}

function FunnelStep({ label, value, hint }: { label: string; value: string; hint: string }) {
  return (
    <div className="al-admin-funnel-step">
      <div className="al-admin-funnel-value">{value}</div>
      <div className="al-admin-funnel-label">{label}</div>
      <div className="tiny muted">{hint}</div>
    </div>
  );
}
